import { useMemo, useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import { invoiceStatusAction } from 'constants-ts/payments';
import callAzureFunctionAuth from 'utils-ts/call-azure-function-auth';
import handleError from 'utils-ts/handle-error';
import { type Booking } from 'types/booking';
import { type Payment, type Invoice } from '@a1/shared';

interface UseInvoiceStatusActionsProps {
  setOpenMarkAsPaidDialog: (open: boolean) => void;
  setOpenRefundDialog?: (open: boolean) => void;
  setOpenVoidDialog?: (open: boolean) => void;
  setSelectedInvoice: (invoice: Invoice | null) => void;
  setExternalRefNo: (ref: string) => void;
  setExternalSource: (source: string) => void;
  setLoading: (loading: boolean) => void;
  setInvoices: React.Dispatch<React.SetStateAction<Invoice[]>>;
  setBookings?: React.Dispatch<React.SetStateAction<Booking[]>>;
}

const useInvoiceStatusActions = ({
  setOpenMarkAsPaidDialog,
  setOpenRefundDialog,
  setOpenVoidDialog,
  setSelectedInvoice,
  setExternalRefNo,
  setExternalSource,
  setLoading,
  setInvoices,
  setBookings
}: UseInvoiceStatusActionsProps) => {
  const cleanupMap = useMemo(
    () => ({
      [invoiceStatusAction.MARK_AS_PAID]: () => {
        setOpenMarkAsPaidDialog(false);
      },
      [invoiceStatusAction.REFUNDED]: () => {
        if (setOpenRefundDialog) {
          setOpenRefundDialog(false);
        }
      },
      [invoiceStatusAction.VOID]: () => {
        setOpenVoidDialog?.(false);
      }
    }),
    [setOpenMarkAsPaidDialog, setOpenRefundDialog, setOpenVoidDialog]
  );

  const processInvoiceStatusChange = useCallback(
    async (
      invoiceId: number,
      action: (typeof invoiceStatusAction)[keyof typeof invoiceStatusAction],
      additionalData: { externalRefNo?: string; externalSource?: string } = {}
    ) => {
      try {
        setLoading(true);
        const payload = {
          invoiceId,
          action,
          ...additionalData
        };

        const response = await callAzureFunctionAuth({
          url: `invoice-status-change`,
          method: 'post',
          data: payload
        });

        let status;
        let invoice;
        if (action !== invoiceStatusAction.VOID) {
          const payment = response.data as Payment;
          invoice = payment.Invoice!;
          ({ status } = invoice);
        } else {
          invoice = response.data as Invoice;
          ({ status } = invoice);
        }

        setInvoices((prevInvoices) => prevInvoices.map((invoice) => (Number(invoice.id) === invoiceId ? { ...invoice, status } : invoice)));

        setBookings?.((prevBookings: Booking[]) =>
          prevBookings.map((booking) => ({
            ...booking,
            Invoices: booking.Invoices?.map((inv: Invoice) => (Number(inv.id) === invoiceId ? { ...inv, status } : inv))
          }))
        );

        let successMessage = '';
        switch (action) {
          case invoiceStatusAction.MARK_AS_PAID:
            successMessage = `Invoice ${invoiceId} marked as 'Paid' successfully.`;
            break;
          case invoiceStatusAction.REFUNDED:
            successMessage = `Invoice ${invoiceId} was refunded successfully.`;
            break;
          case invoiceStatusAction.VOID:
            successMessage = `Invoice ${invoiceId} has been voided successfully.`;
            break;
          default:
            successMessage = `Invoice ${invoiceId} status updated successfully.`;
        }

        enqueueSnackbar({
          variant: 'success',
          message: successMessage,
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
      } catch (error) {
        enqueueSnackbar({
          variant: 'error',
          message: handleError(error),
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
      } finally {
        setLoading(false);
        setSelectedInvoice(null);
        setExternalRefNo('');
        setExternalSource('');
        const cleanup = cleanupMap[action];
        if (cleanup) {
          cleanup();
        }
      }
    },
    [setLoading, setInvoices, setBookings, setSelectedInvoice, setExternalRefNo, setExternalSource, cleanupMap]
  );

  return { processInvoiceStatusChange };
};

export default useInvoiceStatusActions;
